<template>
  <ak-card-header class="py-small">
    <div v-if="isOwnAccount()" class="pull-right">
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <a
            class="icon-action icon-action--inline icon-action--offset icon-cog color-grey-light"
            @click="changeWidget()"
          ></a>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
    <ak-select
      class="width-300 color-grey text-bold h4"
      inline-edit="true"
      track-by="name"
      label="name"
      :model-value="selectedAccount"
      :options="accounts"
      max-height="200"
      placeholder="Select Account"
      @select="selectAccount"
    />
  </ak-card-header>
  <ak-card-body :size="'flush'" class="flex--column overflow-y flex__item">
    <ak-display-table v-if="isLoading" cosy>
      <ak-display-table-row
        v-for="number in n"
        :key="number"
        class="c-skeleton-loading"
      >
        <ak-display-table-cell class="width-40">
          <div
            class="circle circle--large bg-grey-lighter c-skeleton-loading__item"
          ></div>
        </ak-display-table-cell>
        <ak-display-table-cell>
          <ak-pack>
            <ak-pack-item>
              <div class="c-faux-text width-125" />
              <div>
                <p class="c-faux-text width-100 mb-none"></p>
              </div>
            </ak-pack-item>
          </ak-pack>
        </ak-display-table-cell>
        <ak-display-table-cell class="cell--1">
          <div class="c-faux-text width-50" />
          <div class="c-faux-text width-50" />
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>
    <ak-display-table v-model:data="leads" hover>
      <ak-display-table-row v-for="lead in leads" :key="lead._id">
        <ak-display-table-cell class="width-40">
          <div class="p-small">
            <ak-avatar :string="lead.name" />
          </div>
        </ak-display-table-cell>
        <ak-display-table-cell>
          {{ lead.name || 'No name given' }}
          <br />
          <span v-if="lead.formFields.email" class="text-small">
            {{ lead.formFields.email.value }}
          </span>
          <span v-if="!lead.formFields.email" class="text-small"> – </span>
        </ak-display-table-cell>
        <ak-display-table-cell class="text-nowrap px cell--1">
          <strong class="text-small">{{ formatDate(lead.createdOn) }}</strong>
          <br />
          <em class="text-small">{{ formatTime(lead.createdOn) }}</em>
        </ak-display-table-cell>
      </ak-display-table-row>
    </ak-display-table>
    <ak-empty-state
      v-if="!leads.length"
      :title="'Account ' + leadsBranding"
      icon="people"
    >
      There are currently no {{ leadsBranding }} on this account
    </ak-empty-state>
  </ak-card-body>
</template>

<script>
import { ref, computed, onMounted, defineComponent } from 'vue'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import DisplayTable from '@akerolabs/akero-vue/src/components/displayTable/DisplayTable.vue'
import DisplayTableRow from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableRow.vue'
import DisplayTableCell from '@akerolabs/akero-vue/src/components/displayTable/DisplayTableCell.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src/components/toast'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import date from '@akerolabs/akero-vue/src/utils/date'
import { isOwnAccount } from '@akerolabs/akero-vue/src/services/users'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'

export default defineComponent({
  name: 'AkWidgetAgencyAccountLeads',
  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [Box.name]: Box,
    [Form.name]: Form,
    [Input.name]: Input,
    [DisplayTable.name]: DisplayTable,
    [DisplayTableRow.name]: DisplayTableRow,
    [DisplayTableCell.name]: DisplayTableCell,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Function}
     * @description Update the widget state
     */
    changeWidget: {
      type: Function,
      default: () => ({}),
    },
  },
  setup() {
    let n = 10
    const accounts = ref([])
    const leads = ref([])
    const isLoading = ref(true)
    const selectedAccount = ref(accountObj())

    /**
     * @description User defined lead branding
     */
    const leadsBranding = computed(() => {
      return entityBranding('leads.plural', 'Leads')
    })

    /**
     * @description Makes an api request to get the accounts
     */
    const fetchAccounts = async () => {
      isLoading.value = true
      let accountsData = []

      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load accounts`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/accounts', {
          params: {
            agency: agencyObj()._id,
            deleted: JSON.stringify({ $ne: true }),
            per_page: 0,
          },
        })
        accountsData = data.data
      } catch (ex) {
        accountsData = []
        displayError(ex)
      }
      accounts.value = accountsData
      await fetchLeads()
      isLoading.value = false
    }

    // Format Created On Date
    const formatDate = (dateString) => {
      return date(dateString).format('DD/MM/YY')
    }

    // Format Created On Time
    const formatTime = (dateString) => {
      return date(dateString).format('hh:mm a')
    }

    const selectAccount = (selected) => {
      isLoading.value = true
      selectedAccount.value = selected
      fetchLeads()
    }

    const fetchLeads = async () => {
      let leadsData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load requests`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/leads', {
          params: {
            account: selectedAccount.value._id,
            agency: agencyObj()._id,
            deleted: JSON.stringify({ $ne: true }),
            archived: JSON.stringify({ $ne: true }),
            test: JSON.stringify({ $ne: true }),
            isTest: JSON.stringify({ $ne: true }),
            isSpam: JSON.stringify({ $ne: true }),
            per_page: 20,
          },
        })
        leadsData = data.data
      } catch (ex) {
        leadsData = []
        displayError(ex)
      }
      leads.value = leadsData
      isLoading.value = false
    }

    /**
     * @description On mount fetch accounts
     */
    onMounted(async () => {
      await fetchAccounts()
    })

    return {
      accounts,
      leads,
      isLoading,
      formatDate,
      formatTime,
      isOwnAccount,
      n,
      selectedAccount,
      selectAccount,
      leadsBranding,
    }
  },
})
</script>
